/**
 * Candidate · the two decisions background blur must never get wrong, extracted as pure
 * functions so they can be tested without a camera, a GPU or a browser.
 *
 *  1. WHICH CATEGORY IS THE BACKGROUND. Get this backwards and we blur the candidate's face and
 *     leave their room sharp — a worse version of the bug this feature exists to fix. So it is
 *     measured from the frame, never assumed from a model's documented class order, and when the
 *     frame doesn't answer clearly we say "don't know" and the caller disables the feature.
 *  2. WHETHER THE DEVICE CAN AFFORD IT. Segmentation shares a laptop with MediaRecorder and
 *     SpeechRecognition. A candidate's interview must never stutter for a cosmetic toggle.
 */

/** Just enough of a segmentation mask to reason about — one category index per pixel. */
export interface MaskFrameLike {
  data: Uint8Array;
  width: number;
  height: number;
}

// --- polarity -----------------------------------------------------------------------------
/** How thick the border ring is, as a fraction of the frame's shorter side. */
export const RING_BAND_FRACTION = 0.08;
/** The ring has to be this decisively one category before we'll believe it. */
export const RING_MAJORITY = 0.7;
/** Below this, there's barely any background — more likely we've got the polarity inverted. */
export const MIN_BACKGROUND_FRACTION = 0.2;
/** Above this, there's no visible person — nothing to protect from the blur, so don't start. */
export const MAX_BACKGROUND_FRACTION = 0.97;

/**
 * Which mask value means "background"?
 *
 * In a webcam self-view the outer ring of the frame is background — a person's silhouette does
 * not fill the border. So the category dominating the ring is the background, accepted only if
 * the ring is decisively one category AND the resulting split is plausible (a visible person in
 * front of a visible background). Returns null when the frame can't answer, so the caller can
 * try the next frame and eventually give up honestly instead of guessing.
 *
 * Deliberately makes no assumption about the encoding: the selfie segmenter's category values
 * could be {0,1} or {0,255} and either could be the person — all of that is measured, not read
 * off a doc page that might be wrong for the model file we actually shipped.
 */
export function calibrateBackgroundCategory(frame: MaskFrameLike): number | null {
  const { data, width: w, height: h } = frame;
  const total = w * h;
  if (w <= 0 || h <= 0 || data.length < total) return null;

  const band = Math.max(1, Math.round(Math.min(w, h) * RING_BAND_FRACTION));
  const ring: number[] = [];
  const whole: number[] = [];
  let ringTotal = 0;
  for (let y = 0; y < h; y++) {
    const edgeRow = y < band || y >= h - band;
    const rowStart = y * w;
    for (let x = 0; x < w; x++) {
      const v = data[rowStart + x] ?? 0;
      whole[v] = (whole[v] ?? 0) + 1;
      if (edgeRow || x < band || x >= w - band) {
        ring[v] = (ring[v] ?? 0) + 1;
        ringTotal += 1;
      }
    }
  }
  if (ringTotal === 0) return null;

  let best = -1;
  let bestCount = 0;
  ring.forEach((count, value) => {
    if (count > bestCount) {
      bestCount = count;
      best = value;
    }
  });
  if (best < 0 || bestCount / ringTotal < RING_MAJORITY) return null;

  const backgroundFraction = (whole[best] ?? 0) / total;
  if (backgroundFraction < MIN_BACKGROUND_FRACTION || backgroundFraction > MAX_BACKGROUND_FRACTION) return null;
  return best;
}

// --- frame budget -------------------------------------------------------------------------
/** A self-view, not the recording. 15 looks fine and leaves the CPU to speech and encoding. */
export const TARGET_FPS = 15;
export const FRAME_INTERVAL_MS = 1000 / TARGET_FPS;
/** Of the ~66ms each frame gets, spending more than this means we're crowding the tab. */
export const FRAME_BUDGET_MS = 55;
/** The first frames pay for shader compilation and JIT — don't condemn a fine laptop on those. */
export const WARMUP_FRAMES = 12;
/** ~1.7s of sustained overrun before we give up. Long enough to ride out one background task. */
export const SLOW_STREAK = 25;
const EMA_DECAY = 0.85;

export interface FrameBudget {
  frames: number;
  ema: number;
  slowStreak: number;
}

export const initialFrameBudget = (): FrameBudget => ({ frames: 0, ema: 0, slowStreak: 0 });

/**
 * Folds one frame's processing time into the running budget. `tooSlow` means the device has been
 * over budget long enough that we should switch blur off rather than keep stuttering.
 *
 * The streak is measured on the smoothed average, not on single frames, so a GC pause or a tab
 * waking up is forgiven. The EMA's decay counts towards the streak deliberately — a recent
 * average that is still bad IS still bad — which puts the forgiveness window at roughly a second
 * of thrash before a device is judged unable to hold the rate.
 */
export function observeFrameTime(prev: FrameBudget, dtMs: number): { next: FrameBudget; tooSlow: boolean } {
  const frames = prev.frames + 1;
  if (frames <= WARMUP_FRAMES) return { next: { ...prev, frames }, tooSlow: false };
  const ema = prev.ema === 0 ? dtMs : prev.ema * EMA_DECAY + dtMs * (1 - EMA_DECAY);
  const slowStreak = ema > FRAME_BUDGET_MS ? prev.slowStreak + 1 : 0;
  return { next: { frames, ema, slowStreak }, tooSlow: slowStreak >= SLOW_STREAK };
}
